import { useRef, useState } from "react";
import { ScanLine, Upload, Receipt, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const PAID = [["carrier", "We paid (carrier)", "We'll bill it back to the broker"], ["broker", "Broker paid", "For the record only"]];

// Driver: pick who paid (+ amount), then scan or upload the receipt.
export const LumperPanel = ({ busy, onScan, onFile, onCancel }) => {
  const fileRef = useRef(null);
  const [paidBy, setPaidBy] = useState(null);
  const [amount, setAmount] = useState("");
  const needsAmount = paidBy === "carrier";
  const ready = paidBy && (!needsAmount || parseFloat(amount) > 0);
  const meta = { paid_by: paidBy, amount: amount.trim() };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-4" data-testid="lumper-panel">
      <div className="flex items-center justify-between">
        <div className="font-semibold flex items-center gap-2" style={{ fontFamily: "Outfit" }}><Receipt className="w-4 h-4 text-[#002FA7]" /> Lumper receipt</div>
        <button type="button" onClick={onCancel} className="p-1 text-slate-400" aria-label="Cancel" data-testid="lumper-cancel-btn"><X className="w-5 h-5" /></button>
      </div>
      <div>
        <div className="text-sm font-medium mb-2">Who paid the lumper?</div>
        <div className="grid grid-cols-2 gap-2">
          {PAID.map(([k, label, sub]) => (
            <button key={k} type="button" onClick={() => setPaidBy(k)} aria-pressed={paidBy === k}
              className={`rounded-xl border-2 p-3 text-left transition-colors ${paidBy === k ? "border-[#002FA7] bg-blue-50" : "border-slate-200 active:bg-slate-50"}`}
              data-testid={`lumper-paid-${k}`}>
              <div className="text-sm font-semibold">{label}</div>
              <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>
            </button>
          ))}
        </div>
      </div>
      {paidBy && (
        <label className="block">
          <span className="text-sm font-medium">Amount on the receipt {needsAmount ? "" : "(optional)"}</span>
          <div className="relative mt-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
            <Input type="number" inputMode="decimal" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" className="h-12 pl-7 text-base" data-testid="lumper-amount-input" />
          </div>
        </label>
      )}
      <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) onFile(f, meta); }} data-testid="lumper-file-input" />
      <div className="grid grid-cols-2 gap-2">
        <Button onClick={() => onScan(meta)} disabled={!ready || busy} className="h-12 bg-[#002FA7] hover:bg-[#001A75] rounded-xl" data-testid="lumper-scan-btn">
          <ScanLine className="w-4 h-4 mr-1.5" /> Scan
        </Button>
        <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={!ready || busy} className="h-12 rounded-xl" data-testid="lumper-upload-btn">
          <Upload className="w-4 h-4 mr-1.5" /> {busy ? "Uploading…" : "Photo / PDF"}
        </Button>
      </div>
      {!ready && <p className="text-xs text-slate-500 -mt-2" data-testid="lumper-hint">{paidBy ? "Enter the amount we paid first." : "Pick who paid first."}</p>}
    </div>
  );
};

export const PaidByBadge = ({ doc }) => (
  doc.paid_by === "carrier" ? (
    <span className="pill bg-amber-50 text-amber-800 border border-amber-200" data-testid={`lumper-badge-${doc.id}`}>We paid{doc.amount ? ` · $${Number(doc.amount).toFixed(2)}` : ""}</span>
  ) : (
    <span className="pill bg-slate-100 text-slate-600 border border-slate-200" data-testid={`lumper-badge-${doc.id}`}>Broker paid{doc.amount ? ` · $${Number(doc.amount).toFixed(2)}` : ""}</span>
  )
);
